import React, { useContext, useEffect, useState, useMemo, useRef } from 'react';
import AOS from 'aos';
import { Container } from 'react-bootstrap';
import { Link, useLocation } from 'react-router-dom';
import Footer from '../../Footer';
import { store } from '../../../App';
import axios from 'axios';
import parse from 'html-react-parser';
import LoaderComponent from '../../Loader';
import '../../../css/VehicleDetails.css';
import { apiUrl } from '../../Constants';
import BannerCaptionOverlay from '../../BannerCaptionOverlay';
import {
  FaHome,
  FaChevronRight,
  FaTimes,
  FaFileDownload,
  FaTachometerAlt,
  FaTruck
} from 'react-icons/fa';

const FALLBACK_FORM_MENUS = [
  {
    id: 1,
    menu_name_en: 'REQUEST A QUOTE',
    menu_name_ar: 'طلب عرض سعر',
    slug: 'request-a-quote',
    link: 'request-a-quote'
  },
  {
    id: 2,
    menu_name_en: 'BOOK A TEST DRIVE',
    menu_name_ar: 'احجز تجربة قيادة',
    slug: 'book-a-test-drive',
    link: 'book-a-test-drive'
  },
  {
    id: 3,
    menu_name_en: 'DOWNLOAD BROCHURE',
    menu_name_ar: 'طلب كتيب المواصفات',
    slug: 'request-brochure',
    link: 'request-brochure'
  }
];

const resolveFormLink = (item) => {
  if (!item) return '#';
  const raw = item.link || item.slug || '';
  if (raw.startsWith('http://') || raw.startsWith('https://')) return raw;
  if (raw.startsWith('/')) return raw;
  return `/${raw}`;
};

const getDetailBtnIcon = (item) => {
  if (item?.icon_file && typeof item.icon_file === 'string') {
    const trimmed = item.icon_file.trim();
    if (trimmed && trimmed !== 'null' && trimmed !== 'undefined') {
      const isLocal = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
      const base = isLocal ? 'http://localhost/foton-oman-backend/public' : 'https://dev10.sbagh.com/public';
      const src = (trimmed.startsWith('http://') || trimmed.startsWith('https://') || trimmed.startsWith('/'))
        ? trimmed
        : `${base}/images/menus/thumb/${trimmed}`;
      return <img src={src} alt="" style={{ width: 16, height: 16, objectFit: 'contain' }} className="foton-btn-icon-img" />;
    }
  }

  const s = ((item?.slug || '') + ' ' + (item?.menu_name_en || '')).toLowerCase();
  if (s.includes('quote') || s.includes('price')) {
    return <FaTruck />;
  }
  if (s.includes('drive') || s.includes('wheel') || s.includes('test')) {
    return <FaTachometerAlt />;
  }
  if (s.includes('brochure') || s.includes('download') || s.includes('pdf')) {
    return <FaFileDownload />;
  }
  return <FaTruck />;
};

const safeParse = (htmlString, fallback = null) => {
  if (typeof htmlString === 'string' && htmlString.trim().length > 0) {
    try {
      return parse(htmlString);
    } catch (e) {
      console.error("HTML parse error:", e);
      return fallback;
    }
  }
  return fallback;
};

const cleanText = (htmlString) => {
  if (!htmlString || typeof htmlString !== 'string') return '';
  return htmlString
    .replace(/&nbsp;|&#160;/gi, ' ')
    .replace(/<[^>]*>?/gm, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

const renderContent = (htmlString, fallback = '') => {
  if (!htmlString || typeof htmlString !== 'string') return fallback;
  // If text contains translation widget artifacts or inline pre tags, clean to plain text
  if (
    htmlString.includes('tw-data-text') ||
    htmlString.includes('tw-target-rmn') ||
    htmlString.includes('<pre') ||
    htmlString.includes('font-size: 28px')
  ) {
    return cleanText(htmlString);
  }
  return safeParse(htmlString, cleanText(htmlString) || fallback);
};

const renderRichDescription = (htmlString, fallback = '') => {
  if (!htmlString || typeof htmlString !== 'string') return fallback;
  const trimmed = htmlString.trim();
  if (!trimmed) return fallback;

  // Clean empty <br> container divs that WYSIWYG editors inject between paragraphs
  const cleaned = trimmed
    .replace(/<div>\s*<br\s*\/?>\s*<\/div>/gi, '')
    .replace(/<p>\s*<br\s*\/?>\s*<\/p>/gi, '')
    .trim();

  // If text contains HTML tags (<p>, <br>, <div>, etc.), parse into React elements
  if (/<(p|br|div|ul|ol|li|h[1-6]|span|strong|em|b|i)[^>]*>/i.test(cleaned)) {
    return renderContent(cleaned, fallback);
  }

  // If plain text with newline separators, split into clean paragraphs
  if (cleaned.includes('\n')) {
    const paras = cleaned
      .split(/\r?\n+/)
      .map((p) => p.trim())
      .filter(Boolean);
    if (paras.length > 1) {
      return paras.map((p, idx) => <p key={idx}>{p}</p>);
    }
  }

  return <p>{cleaned}</p>;
};

const hasMeaningfulText = (htmlString) => {
  if (!htmlString || typeof htmlString !== 'string') return false;
  const text = cleanText(htmlString);
  if (!text) return false;
  const lower = text.toLowerCase();
  return !(
    lower === '-' ||
    lower === '—' ||
    lower === '–' ||
    lower === 'null' ||
    lower === 'undefined' ||
    lower === 'none' ||
    lower === 'n/a'
  );
};

const getFeatureSummaries = (item, lang) => {
  if (!item) return [];
  return [
    lang ? (item.summery1_ar || item.summery1_en) : item.summery1_en,
    lang ? (item.summery2_ar || item.summery2_en) : item.summery2_en,
    lang ? (item.summery3_ar || item.summery3_en) : item.summery3_en,
    lang ? (item.summery4_ar || item.summery4_en) : item.summery4_en,
  ].filter(hasMeaningfulText);
};

/** Ucfirst slug → API content key (design → Design) */
const toFeatureDataKey = (slug) => {
  const s = String(slug || '').toLowerCase();
  if (!s) return '';
  return s.charAt(0).toUpperCase() + s.slice(1);
};

/**
 * TAB / SECTION order only — from features[] master row.
 * Prefer section_sort_order, then features[i].sort_order, else array index.
 * Never use content-row sort_order here.
 */
const getFeatureSectionSortOrder = (feat, index) => {
  const raw = feat?.section_sort_order ?? feat?.sort_order;
  if (raw !== undefined && raw !== null && raw !== '') {
    const n = Number(raw);
    if (!Number.isNaN(n)) return n;
  }
  return (typeof index === 'number' ? index : 0) + 1;
};

/** CONTENT order inside a tab — ascending row.sort_order */
const sortFeatureContentRows = (rows) =>
  [...(Array.isArray(rows) ? rows : [])].sort(
    (a, b) => (a.sort_order ?? 99) - (b.sort_order ?? 99)
  );

/**
 * Load data[Ucfirst(slug)] (with Comfort/Interior, Performance/Reliable aliases).
 * Returns rows sorted by item.sort_order only.
 */
const resolveFeatureContentRows = (detailsData, feat) => {
  if (!detailsData || !feat) return [];
  const slug = (feat.slug || '').toLowerCase();
  const primary = toFeatureDataKey(slug);
  const aliases = {
    comfort: ['Comfort', 'Interior'],
    interior: ['Interior', 'Comfort'],
    reliable: ['Reliable', 'Performance'],
    performance: ['Performance', 'Reliable'],
    safe: ['Safe', 'Safety'],
    safety: ['Safety', 'Safe'],
    gallery: ['Gallery'],
  };
  const keys = aliases[slug] || (primary ? [primary] : []);
  for (let i = 0; i < keys.length; i += 1) {
    const list = detailsData[keys[i]];
    if (Array.isArray(list) && list.length > 0) {
      return sortFeatureContentRows(list);
    }
  }
  return [];
};

/** Bullet list for zigzag feature rows — summary fields only (1–15) */
const getFeatureZigzagBullets = (item, lang) => {
  if (!item) return [];
  const list = [];
  for (let i = 1; i <= 15; i += 1) {
    const raw = lang
      ? (item[`summery${i}_ar`] || item[`summery${i}_en`])
      : item[`summery${i}_en`];
    if (hasMeaningfulText(raw)) list.push(raw);
  }
  return list;
};

const isTruthyFlag = (value) =>
  value === true || value === 1 || value === '1' || value === 'true' || value === 'yes';

/** Optional full-bleed feature banner — only when backend enables it */
const isFeatureBannerEnabled = (item) => {
  if (!item) return false;
  return (
    isTruthyFlag(item.is_banner_enabled) ||
    isTruthyFlag(item.show_banner) ||
    isTruthyFlag(item.is_banner) ||
    String(item.display_type || item.feature_display_type || '').toLowerCase() === 'banner'
  );
};

/** Optional Product 360 / Design 360 block — separate toggle from banner */
const isDesign360Enabled = (item) => {
  if (!item) return false;
  if (
    isTruthyFlag(item.is_360_enabled) ||
    isTruthyFlag(item.is_design_360_enabled) ||
    isTruthyFlag(item.show_360) ||
    isTruthyFlag(item.is_360)
  ) {
    return true;
  }
  const displayType = String(item.display_type || item.feature_display_type || '').toLowerCase();
  if (['360', 'design_360', 'product_360'].includes(displayType)) return true;

  // Title hint: CMS often names the row "PRODUCT 360" on Design (or any) feature
  const titleHint = [
    item.feature_title_en,
    item.feature_title_ar,
    item.design_360_title_en,
    item.design_360_title_ar,
    item.banner_title_en,
    item.banner_title_ar,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return /\b360\b|product\s*360|design\s*360/.test(titleHint);
};

/** True when any product-feature row has banner or 360 — used to hide auto Overview/Glance */
const productHasAnyFeatureBanner = (detailsData) => {
  if (!detailsData) return false;
  if ((detailsData.features || []).some((f) => isFeatureBannerEnabled(f) || isDesign360Enabled(f))) {
    return true;
  }

  const skipKeys = new Set([
    'title', 'seo', 'features', 'features_title',
    'product_galleries', 'product_configurations', 'product_configuraiton_title', 'Gallery'
  ]);

  return Object.keys(detailsData).some((key) => {
    if (skipKeys.has(key)) return false;
    const value = detailsData[key];
    return Array.isArray(value) && value.some((item) => isFeatureBannerEnabled(item) || isDesign360Enabled(item));
  });
};

// URL helpers for backend images
let activeAssetBase = '';

// Filter out static external placeholder images seeded from fotonmotor.com
const isStaticSeededImage = (img) => {
  if (!img || typeof img !== 'string') return false;
  return img.includes('fotonmotor.com');
};

// Match image CDN to configured API host so CMS assets (Wonder slider, etc.) load in local frontend
const getPublicAssetBase = () => {
  if (activeAssetBase) return activeAssetBase;
  if (apiUrl && /^https?:\/\//i.test(apiUrl)) {
    try {
      const origin = new URL(apiUrl).origin;
      return `${origin}/public`;
    } catch (e) {
      // fall through
    }
  }
  return 'https://dev10.sbagh.com/public';
};

const getProductImageUrl = (img) => {
  if (!img || typeof img !== 'string') return '/Images/Card-img/AUMAN-EST-A-ICON.webp';
  const trimmed = img.trim();
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://') || trimmed.startsWith('/')) {
    return trimmed;
  }
  if (trimmed.startsWith('auman_est_m_')) {
    return `/Images/products/auman-est-m/${trimmed}`;
  }
  return `${getPublicAssetBase()}/images/products/large/${trimmed}`;
};

const getGalleryImageUrl = (img) => {
  if (!img || typeof img !== 'string' || isStaticSeededImage(img)) return null;
  const trimmed = img.trim();
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://') || trimmed.startsWith('/')) {
    return trimmed;
  }
  if (trimmed.startsWith('auman_est_m_')) {
    return `/Images/products/auman-est-m/${trimmed}`;
  }
  return `${getPublicAssetBase()}/images/product-gallery/large/${trimmed}`;
};

const resolveFeatureMediaFile = (item, fileName) => {
  if (!item || !fileName || typeof fileName !== 'string') return null;
  const trimmed = fileName.trim();
  if (!trimmed || isStaticSeededImage(trimmed)) return null;
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://') || trimmed.startsWith('/')) {
      return trimmed;
    }
    if (trimmed.startsWith('auman_est_m_')) {
      return `/Images/products/auman-est-m/${trimmed}`;
    }
  const base = getPublicAssetBase();
    if (item.products_id && item.master_features_id) {
      return `${base}/images/products/${item.products_id}/${item.master_features_id}/large/${trimmed}`;
    }
    return `${base}/images/products/large/${trimmed}`;
};

const getFeatureImageUrl = (item) => {
  if (!item || isStaticSeededImage(item.vehicle_image)) return null;
  return resolveFeatureMediaFile(item, item.vehicle_image);
};

const getFeatureBannerImageUrl = (item) => {
  if (!item) return null;
  return (
    resolveFeatureMediaFile(item, item.banner_image) ||
    resolveFeatureMediaFile(item, item.vehicle_image)
  );
};

const resolveFeatureBannerData = (masterFeature, items, lang) => {
  // 1) Master feature-level banner (features[] from API)
  if (masterFeature && isFeatureBannerEnabled(masterFeature)) {
    const bannerFile = masterFeature.banner_image || masterFeature.banner_image_en;
    const title = lang
      ? (masterFeature.banner_title_ar || masterFeature.display_name_ar || masterFeature.display_name_en)
      : (masterFeature.banner_title_en || masterFeature.display_name_en);
    const overlay = lang
      ? (masterFeature.banner_overlay_text_ar || masterFeature.banner_overlay_text_en)
      : masterFeature.banner_overlay_text_en;
    const description = lang
      ? (masterFeature.banner_description_ar || masterFeature.banner_description_en)
      : (masterFeature.banner_description_en || masterFeature.banner_description_ar);
    const productId = masterFeature.products_id || items?.[0]?.products_id;
    const image =
      resolveFeatureMediaFile(
        { products_id: productId, master_features_id: masterFeature.id },
        bannerFile
      ) || (bannerFile ? `${getPublicAssetBase()}/images/products/large/${bannerFile}` : null);

    if (image) {
      const bannerItem = (items || []).find(
        (item) => isFeatureBannerEnabled(item) && !isDesign360Enabled(item)
      );
      return {
        source: 'master',
        title: cleanText(title || ''),
        overlay: hasMeaningfulText(overlay) ? cleanText(overlay) : '',
        description: hasMeaningfulText(description) ? description.trim() : '',
        image,
        itemId: bannerItem?.id || null,
      };
    }
  }

  // 2) First content row marked as banner type
  // Product 360 rows win over full-bleed banner for the same feature item
  const bannerItem = (items || []).find(
    (item) => isFeatureBannerEnabled(item) && !isDesign360Enabled(item)
  );
  if (!bannerItem) return null;

  const image = getFeatureBannerImageUrl(bannerItem);
  if (!image) return null;

  // Prefer explicit banner title → master tab name (DESIGN) → feature row title
  // so Design tab doesn't show "PRODUCT 360" while Performance shows "PERFORMANCE"
  const title = lang
    ? (bannerItem.banner_title_ar
      || masterFeature?.display_name_ar
      || masterFeature?.display_name_en
      || bannerItem.feature_title_ar
      || bannerItem.feature_title_en)
    : (bannerItem.banner_title_en
      || masterFeature?.display_name_en
      || bannerItem.feature_title_en
      || bannerItem.feature_title_ar);
  const overlay = lang
    ? (bannerItem.banner_overlay_text_ar || bannerItem.banner_overlay_text_en)
    : bannerItem.banner_overlay_text_en;
  const description = lang
    ? (bannerItem.banner_description_ar
      || bannerItem.banner_description_en
      || masterFeature?.banner_description_ar
      || masterFeature?.banner_description_en)
    : (bannerItem.banner_description_en
      || bannerItem.banner_description_ar
      || masterFeature?.banner_description_en
      || masterFeature?.banner_description_ar);

  return {
    source: 'item',
    title: cleanText(title || ''),
    overlay: hasMeaningfulText(overlay) ? cleanText(overlay) : '',
    description: hasMeaningfulText(description) ? description.trim() : '',
    image,
    itemId: bannerItem.id,
  };
};

const resolveDesign360Frames = (item) => {
  if (!item) return [];
  const frames = [];
  const seen = new Set();
  const pushFrame = (file, id) => {
    const src = resolveFeatureMediaFile(item, file);
    if (!src || seen.has(src)) return;
    seen.add(src);
    frames.push({ id: id || file, src });
  };

  // Dedicated 360 frame list (preferred)
  const dedicated =
    item.design_360_images ||
    item.images_360 ||
    item.product_360_images ||
    item.frames_360 ||
    [];
  if (Array.isArray(dedicated) && dedicated.length > 0) {
    dedicated.forEach((g, idx) => {
      const file = typeof g === 'string' ? g : (g.image || g.file || g.url);
      if (file) pushFrame(file, g.id || `360-${idx}`);
    });
  }

  // Feature gallery frames (sorted)
  const gallery = Array.isArray(item.product_feature_gallery)
    ? [...item.product_feature_gallery].sort((a, b) => (a.sort_order ?? 99) - (b.sort_order ?? 99))
    : [];
  gallery.forEach((g, idx) => {
    const file = typeof g === 'string' ? g : (g.image || g.file || g.url);
    if (file) pushFrame(file, g.id || `gallery-${idx}`);
  });

  if (frames.length === 0 && item.design_360_image) pushFrame(item.design_360_image, '360-main');
  if (frames.length === 0 && item.vehicle_image) pushFrame(item.vehicle_image, '360-vehicle');
  if (frames.length === 0 && item.banner_image) pushFrame(item.banner_image, '360-banner');
  return frames;
};

const parseMaybeJsonArray = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }
  return [];
};

const resolveDesign360Specs = (item, lang, productConfigs = []) => {
  if (!item) return [];
  const specs = [];

  const pushSpec = (label, value) => {
    const l = cleanText(label || '');
    const v = cleanText(value || '');
    if (l && v && specs.length < 4) specs.push({ label: l, value: v });
  };

  // JSON array from CMS: design_360_specs / product_360_specs
  const jsonSpecs = [
    ...parseMaybeJsonArray(item.design_360_specs),
    ...parseMaybeJsonArray(item.product_360_specs),
    ...parseMaybeJsonArray(item.specs_360),
  ];
  jsonSpecs.forEach((row) => {
    if (!row || typeof row !== 'object') return;
    const label = lang
      ? (row.label_ar || row.label_en || row.label)
      : (row.label_en || row.label_ar || row.label);
    const value = lang
      ? (row.value_ar || row.value_en || row.value)
      : (row.value_en || row.value_ar || row.value);
    pushSpec(label, value);
  });

  for (let i = 1; i <= 4; i += 1) {
    const label = lang
      ? (item[`design_360_spec${i}_label_ar`] || item[`design_360_spec${i}_label_en`] || item[`spec${i}_label_ar`] || item[`spec${i}_label_en`])
      : (item[`design_360_spec${i}_label_en`] || item[`spec${i}_label_en`]);
    const value = lang
      ? (item[`design_360_spec${i}_value_ar`] || item[`design_360_spec${i}_value_en`] || item[`spec${i}_value_ar`] || item[`spec${i}_value_en`])
      : (item[`design_360_spec${i}_value_en`] || item[`spec${i}_value_en`]);
    pushSpec(label, value);
  }

  if (specs.length > 0) return specs;

  getFeatureSummaries(item, lang).forEach((raw) => {
    const text = cleanText(raw);
    if (!text) return;
    const split = text.split(/\s*[|:：]\s*/);
    if (split.length >= 2) {
      pushSpec(split[0], split.slice(1).join(': '));
    }
  });

  if (specs.length > 0) return specs;

  // Fallback: first product configuration rows (Body Size / Wheelbase style)
  (productConfigs || []).slice(0, 2).forEach((cfg) => {
    const label = lang
      ? (cfg.label_name_ar || cfg.label_name_en)
      : (cfg.label_name_en || cfg.label_name_ar);
    const value = lang
      ? (cfg.value_ar || cfg.value_en)
      : (cfg.value_en || cfg.value_ar);
    pushSpec(label, value);
  });

  return specs;
};

const resolveDesign360Variants = (item) => {
  if (!item) return [];
  const variants = parseMaybeJsonArray(item.design_360_variants || item.product_360_variants || item.color_variants);
  const list = [];
  variants.forEach((v, idx) => {
    if (!v) return;
    const file = typeof v === 'string' ? v : (v.image || v.thumb || v.thumbnail || v.file);
    const src = resolveFeatureMediaFile(item, file);
    if (!src) return;
    list.push({
      id: v.id || `variant-${idx}`,
      src,
      label: v.label_en || v.name_en || v.name || v.color || '',
    });
  });
  if (list.length) return list;

  const gallery = Array.isArray(item.product_feature_gallery) ? item.product_feature_gallery : [];
  gallery.slice(0, 4).forEach((g, idx) => {
    const file = typeof g === 'string' ? g : (g.image || g.file);
    const src = resolveFeatureMediaFile(item, file);
    if (!src) return;
    list.push({ id: g.id || `g-${idx}`, src, label: g.title_en || '' });
  });
  return list;
};

function Inside() {
  const [lang] = useContext(store);
  const [detailsData, setDetailsData] = useState(null);
  const [formMenus, setFormMenus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSubnavStuck, setIsSubnavStuck] = useState(false);
  const [selectedGalleryIdx, setSelectedGalleryIdx] = useState(0);
  const [lightboxImg, setLightboxImg] = useState(null);
  const [design360FrameIdx, setDesign360FrameIdx] = useState({});
  const design360DragRef = useRef({ active: false, key: null, startX: 0, startIdx: 0 });

  const location = useLocation();
  const rawPath = location.pathname.split('/').filter(Boolean).pop() || '';
  const vehicleSlug = rawPath.toLowerCase();

  const [activeTab, setActiveTab] = useState('highlights');

  useEffect(() => {
    setLoading(true);
    setDetailsData(null);

    const remoteUrl = apiUrl
      ? `${apiUrl}getSingleProductDetailsByProductSlug/${vehicleSlug}`
      : `https://dev10.sbagh.com/api/getSingleProductDetailsByProductSlug/${vehicleSlug}`;
    const localUrl = `http://localhost/foton-oman-backend/api/getSingleProductDetailsByProductSlug/${vehicleSlug}`;
    // Always use configured remoteUrl (apiUrl from Constants.js) as primary so CMS updates reflect immediately
    const primaryUrl = remoteUrl;
    const fallbackUrl = localUrl;

    const applyDataWithAssetBase = (data, usedUrl) => {
      if (usedUrl.includes('localhost')) {
        activeAssetBase = 'http://localhost/foton-oman-backend/public';
      } else {
        activeAssetBase = 'https://dev10.sbagh.com/public';
      }
      setDetailsData(data);
    };

    axios.get(primaryUrl)
      .then((response) => {
        if (response.data && response.data.data) {
          applyDataWithAssetBase(response.data.data, primaryUrl);
        } else {
          return axios.get(fallbackUrl).then((fbRes) => {
            if (fbRes.data && fbRes.data.data) {
              applyDataWithAssetBase(fbRes.data.data, fallbackUrl);
            }
          });
        }
      })
      .catch((error) => {
        console.warn('Could not load vehicle details from primary endpoint, trying fallback:', error?.message);
        return axios.get(fallbackUrl)
          .then((response) => {
            if (response.data && response.data.data) {
              applyDataWithAssetBase(response.data.data, fallbackUrl);
            }
          })
          .catch((fbErr) => {
            console.error('Error fetching fallback vehicle details:', fbErr);
          });
      })
      .finally(() => {
        setLoading(false);
      });
  }, [location, vehicleSlug]);

  // Fetch dynamic form menus from api/getFormMenus
  useEffect(() => {
    const remoteFormMenus = apiUrl ? `${apiUrl}getFormMenus` : 'https://dev10.sbagh.com/api/getFormMenus';
    const localFormMenus = 'http://localhost/foton-oman-backend/api/getFormMenus';
    const primaryFormMenus = remoteFormMenus;
    const fallbackFormMenus = localFormMenus;

    axios.get(primaryFormMenus)
      .then((res) => {
        const items = res.data?.data?.menus;
        if (Array.isArray(items)) {
          setFormMenus(items);
        } else {
          return axios.get(fallbackFormMenus).then((fbRes) => {
            const fbItems = fbRes.data?.data?.menus;
            if (Array.isArray(fbItems)) setFormMenus(fbItems);
          });
        }
      })
      .catch(() => {
        axios.get(fallbackFormMenus)
          .then((fbRes) => {
            const items = fbRes.data?.data?.menus;
            if (Array.isArray(items)) {
              setFormMenus(items);
            }
          })
          .catch((err) => {
            console.warn('Could not load dynamic form menus in Inside.js:', err);
            setFormMenus(FALLBACK_FORM_MENUS);
          });
      });
  }, []);

  useEffect(() => {
    if (!loading && detailsData) {
      const timer = setTimeout(() => {
        AOS.refresh();
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [loading, detailsData]);

  // Tab navigation list dynamically generated from backend API features (master_features)
  const navTabs = useMemo(() => {
    if (!detailsData) return [];

    const list = [];
    const hasHighlights = (detailsData.Highlights || []).length > 0;
    const hasFeatureBanner = productHasAnyFeatureBanner(detailsData);

    const cmsFeatureSlugs = new Set(
      (Array.isArray(detailsData.features) ? detailsData.features : [])
        .map((feat) => (feat.slug || '').toLowerCase())
        .filter(Boolean)
    );

    // Fallback overview only when the CMS did not already send a Glance feature
    if (!hasHighlights && !hasFeatureBanner && !cmsFeatureSlugs.has('glance')) {
      list.push({
        slug: 'glance',
        titleEn: 'GLANCE',
        titleAr: 'نظرة عامة',
        sortOrder: 0,
        featureIndex: -1,
      });
    }

    // TAB ORDER = data.features[] order / section_sort_order — never content-row sort_order
    if (Array.isArray(detailsData.features)) {
      detailsData.features.forEach((feat, index) => {
        const slug = (feat.slug || '').toLowerCase();
        if (!slug) return;

        if (slug === 'gallery') {
          const galleryPhotos = detailsData.Gallery || detailsData.product_galleries || [];
          const galleryOn = isTruthyFlag(detailsData.title?.is_gallery_enabled) || galleryPhotos.length > 0;
          if (galleryOn && galleryPhotos.length > 0) {
            list.push({
              slug: 'gallery',
              titleEn: (feat.display_name_en || 'Gallery').toUpperCase(),
              titleAr: feat.display_name_ar || feat.display_name_en || 'المعرض',
              sortOrder: getFeatureSectionSortOrder(feat, index),
              featureIndex: index,
            });
          }
          return;
        }

        const items = resolveFeatureContentRows(detailsData, feat);
        if (items.length > 0 || isFeatureBannerEnabled(feat) || isDesign360Enabled(feat)) {
          list.push({
            slug,
            titleEn: (feat.display_name_en || feat.slug).toUpperCase(),
            titleAr: feat.display_name_ar || feat.display_name_en || feat.slug,
            sortOrder: getFeatureSectionSortOrder(feat, index),
            featureIndex: index,
          });
        }
      });
    }

    // Specs nav only when this product has product_configurations data
    if (
      Array.isArray(detailsData.product_configurations) &&
      detailsData.product_configurations.length > 0
    ) {
      const cfgTitle = detailsData.product_configuraiton_title || {};
      list.push({
        slug: 'specifications',
        titleEn: (cfgTitle.category_title_en || 'SPECIFICATIONS').toUpperCase(),
        titleAr: cfgTitle.category_title_ar || cfgTitle.category_title_en || 'المواصفات',
        sortOrder: 10000,
        featureIndex: 9999,
      });
    }

    // Stable sort by section_sort_order only (ties keep features[] order via featureIndex)
    list.sort(
      (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || (a.featureIndex ?? 0) - (b.featureIndex ?? 0)
    );

    const seenSlugs = new Set();
    return list.filter((tab) => {
      if (seenSlugs.has(tab.slug)) return false;
      seenSlugs.add(tab.slug);
      return true;
    });
  }, [detailsData]);


  useEffect(() => {
    if (navTabs.length > 0 && !navTabs.some((t) => t.slug === activeTab)) {
      setActiveTab(navTabs[0].slug);
    }
  }, [navTabs, activeTab]);

  // ScrollSpy listener to highlight active tab and track sticky subnav
  useEffect(() => {
    if (!detailsData) return;

    const syncActiveTabFromScroll = () => {
      const banner = document.querySelector('.foton-hero-banner');
      if (banner) {
        const bannerRect = banner.getBoundingClientRect();
        setIsSubnavStuck(bannerRect.bottom <= 76);
      }

      // Match tab highlight to real DOM order (offsetTop), not CMS tab list order alone
      const positioned = navTabs
        .map((tab) => {
          const el = document.getElementById(tab.slug);
          return el ? { slug: tab.slug, top: el.offsetTop } : null;
        })
        .filter(Boolean)
        .sort((a, b) => a.top - b.top);

      if (!positioned.length) return;

      // Near page top → first section in the document
      if (window.scrollY < 40) {
        setActiveTab(positioned[0].slug);
        return;
      }

      const scrollPos = window.scrollY + 180;
      let matched = positioned[0].slug;
      for (let i = 0; i < positioned.length; i += 1) {
        if (positioned[i].top <= scrollPos) matched = positioned[i].slug;
      }
      setActiveTab(matched);
    };

    // Initial sync after sections mount (avoids DESIGN selected while PERFORMANCE banner is on screen)
    const timer = setTimeout(syncActiveTabFromScroll, 120);
    window.addEventListener('scroll', syncActiveTabFromScroll, { passive: true });
    return () => {
      clearTimeout(timer);
      window.removeEventListener('scroll', syncActiveTabFromScroll);
    };
  }, [navTabs, detailsData, loading]);

  const handleTabClick = (e, slug) => {
    e.preventDefault();
    setActiveTab(slug);
    const target = document.getElementById(slug);
    if (target) {
      const offset = 140; // offset for sticky subnav & fixed header
      const topPos = target.getBoundingClientRect().top + window.pageYOffset - offset;
      window.scrollTo({
        top: topPos,
        behavior: 'smooth',
      });
    }
  };

  if (loading) {
    return (
      <>
        <LoaderComponent />
        <Footer />
      </>
    );
  }

  if (!detailsData || !detailsData.title) {
    return (
      <Container className="text-center py-5 my-5">
        <h3 className="mb-4">{lang ? 'لم يتم العثور على المنتج' : 'Product Not Found'}</h3>
        <p className="text-muted">{lang ? 'يرجى التحقق من الرابط أو المحاولة لاحقاً.' : 'Please check the URL or try again later.'}</p>
        <Link to="/" className="btn btn-primary mt-3">
          {lang ? 'العودة إلى الصفحة الرئيسية' : 'Back to Home'}
        </Link>
      </Container>
    );
  }

  // Purely dynamic product data from API
  const productName = lang
    ? (detailsData.title.product_name_ar || detailsData.title.product_name_en)
    : (detailsData.title.product_name_en || detailsData.title.product_name_ar);

  const rawShortDesc = lang
    ? (detailsData.title.short_description_ar || detailsData.title.short_description_en)
    : (detailsData.title.short_description_en || detailsData.title.short_description_ar);
  // Treat CMS placeholders like "-" as empty so hero title can use product_slider_title
  const productShortDesc = (() => {
    const cleaned = cleanText(rawShortDesc || '');
    if (!cleaned || cleaned === '-' || cleaned === '—' || cleaned === '–') return '';
    return cleaned;
  })();

  const productSliderTitle = cleanText(
    (lang
      ? (detailsData.title.product_slider_title_ar || detailsData.title.product_slider_title)
      : detailsData.title.product_slider_title) || ''
  );

  const bannerGallery = detailsData.product_galleries?.find((g) =>
    g.image?.toLowerCase()?.includes('banner') ||
    g.image?.toLowerCase()?.includes('slider') ||
    g.title_en?.toLowerCase()?.includes('banner') ||
    g.title_en?.toLowerCase()?.includes('hero') ||
    g.title_en?.toLowerCase()?.includes('empowering')
  );

  // Hero priority: product_slider_image → banner gallery → product_image → feature image
  const heroImage = detailsData.title.product_slider_image
    ? getProductImageUrl(detailsData.title.product_slider_image)
    : (bannerGallery
    ? getGalleryImageUrl(bannerGallery.image)
    : (detailsData.title.product_image
        ? getProductImageUrl(detailsData.title.product_image)
            : (detailsData.Highlights?.[0]?.vehicle_image
                ? getFeatureImageUrl(detailsData.Highlights[0])
        : (detailsData.Exterior?.[0]?.vehicle_image
            ? getFeatureImageUrl(detailsData.Exterior[0])
                    : '/Images/Card-img/MINI-TRUCK-ICON.webp'))));

  const productGalleries = Array.isArray(detailsData.product_galleries)
    ? detailsData.product_galleries.filter((g) => g && g.image && !isStaticSeededImage(g.image))
    : [];

  // Specific dynamic feature arrays from API (supports Highlights, Comfort, Power, Safe, Reliable, Utility & standard)
  const allHighlightItems = [...(detailsData.Highlights || [])].sort(
    (a, b) => (a.sort_order ?? 99) - (b.sort_order ?? 99)
  );
  const highlightsMasterFeature = (detailsData.features || []).find(
    (f) => (f.slug || '').toLowerCase() === 'highlights'
  );
  const highlightsBanner = resolveFeatureBannerData(highlightsMasterFeature, allHighlightItems, lang);
  // Banner-type rows are shown as full-bleed banner only — not repeated as content cards
  const highlightItems = allHighlightItems.filter((item) =>
    highlightsBanner?.itemId
      ? item.id !== highlightsBanner.itemId
      : !isFeatureBannerEnabled(item)
  );
  const hasHighlights = allHighlightItems.length > 0 || Boolean(highlightsBanner);

  // Optional Product Gallery showcase (admin: Show Gallery tab = Yes + Product Gallery photos)
  const galleryPhotos = (() => {
    const fromApi = Array.isArray(detailsData.Gallery) ? detailsData.Gallery : [];
    const fromProduct = Array.isArray(detailsData.product_galleries) ? detailsData.product_galleries : [];
    const photos = (fromApi.length > 0 ? fromApi : fromProduct).filter(
      (p) => p && p.image && !isStaticSeededImage(p.image)
    );
    const enabledInFeatures = (detailsData.features || []).some(
      (f) => (f.slug || '').toLowerCase() === 'gallery'
    );
    const enabledFlag = isTruthyFlag(detailsData.title?.is_gallery_enabled);
    if (!(enabledFlag || enabledInFeatures) || photos.length === 0) return [];
    return [...photos].sort((a, b) => (a.sort_order ?? 99) - (b.sort_order ?? 99));
  })();
  const showProductGallery = galleryPhotos.length > 0;

  const configs = Array.isArray(detailsData.product_configurations) ? detailsData.product_configurations : [];
  const configTitleMeta = detailsData.product_configuraiton_title || null;
  const hasAnyFeatureBanner = productHasAnyFeatureBanner(detailsData);
  const customHighlights = hasHighlights;
  const cmsHasGlance = (detailsData.features || []).some(
    (feat) => (feat.slug || '').toLowerCase() === 'glance'
  );
  // Hide the built-in overview when the CMS already provides Glance, Highlights, or a feature banner
  const showGlanceOverview = !customHighlights && !hasAnyFeatureBanner && !cmsHasGlance;
  const highlightsTabSlug = hasHighlights ? 'highlights' : 'glance';
  const primaryHighlight = highlightItems[0] || allHighlightItems[0] || null;

  // features[] order for nav + section sequence (not alphabetical, not content-row sort_order)
  const orderedMasterFeatures = (() => {
    const list = Array.isArray(detailsData.features) ? [...detailsData.features] : [];
    return list
      .map((feat, index) => ({ feat, index }))
      .sort(
        (a, b) =>
          getFeatureSectionSortOrder(a.feat, a.index) - getFeatureSectionSortOrder(b.feat, b.index)
      )
      .map(({ feat }) => feat);
  })();

  // Never fall back to another vehicle's static asset (AUMAN) — use this product's own images
  const productFallbackSrc = detailsData.title.product_image
    ? getProductImageUrl(detailsData.title.product_image)
    : (detailsData.title.product_slider_image
        ? getProductImageUrl(detailsData.title.product_slider_image)
        : '/Images/Card-img/MINI-TRUCK-ICON.webp');

  const renderFeatureBanner = (banner, sectionId) => {
    if (!banner?.image) return null;
    return (
      <section className="foton-feature-banner" id={sectionId || undefined}>
        <img
          src={banner.image}
          alt={banner.title || productName}
          className="foton-feature-banner-img"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = productFallbackSrc;
          }}
        />
        <div className="foton-feature-banner-shade" />
        {hasMeaningfulText(banner.overlay) && (
          <div className="foton-feature-banner-watermark" aria-hidden="true">
            {banner.overlay}
          </div>
        )}
        <div className="foton-feature-banner-content" data-aos="fade-up">
          {banner.title && <h2 className="foton-feature-banner-title">{banner.title}</h2>}
          {banner.description && (
            <div className="feature-banner__description foton-feature-banner-desc whitespace-pre-line">
              {renderRichDescription(banner.description)}
            </div>
          )}
        </div>
      </section>
    );
  };

  /**
   * Alternating product-feature row (text | image card), matching CMS "more features" mockup.
   * Even rows: text left / image right. Odd rows: image left / text right.
   */
  const renderZigzagFeatureRow = (item, feat, { sectionId, reverse = false, key } = {}) => {
    if (!item) return null;

    const featureTitle = lang
      ? (item.feature_title_ar || item.feature_title_en)
      : (item.feature_title_en || item.feature_title_ar);
    const featureDesc = lang
      ? (item.short_description_ar || item.short_description_en)
      : (item.short_description_en || item.short_description_ar);
    const showShortDesc = hasMeaningfulText(featureDesc);
    const galleryItems = [...(item.product_feature_gallery || [])]
      .filter((g) => g && g.image && !isStaticSeededImage(g.image))
      .sort((a, b) => (a.sort_order ?? 99) - (b.sort_order ?? 99));
    // vehicle_image → products/{id}/{feature}/large; gallery → product-gallery/large (separate strip)
    const featureImg = item.vehicle_image && !isStaticSeededImage(item.vehicle_image)
      ? getFeatureImageUrl(item)
      : null;
    const bullets = getFeatureZigzagBullets(item, lang);
    // Only explicit overlay fields — do not steal gallery item titles onto the hero image
    const overlayCaption = cleanText(
      lang
        ? (item.image_overlay_text_ar || item.image_caption_ar || item.image_overlay_text_en || item.image_caption_en || '')
        : (item.image_overlay_text_en || item.image_caption_en || '')
    );

    const hasMainImage = Boolean(item.vehicle_image && featureImg && !isStaticSeededImage(item.vehicle_image));
    if (!featureTitle && !showShortDesc && bullets.length === 0 && !hasMainImage && galleryItems.length === 0) {
      return null;
    }

    const textBlock = (
      <div className="foton-zigzag-text" data-aos="fade-up">
        {featureTitle && <h2 className="foton-zigzag-title">{featureTitle}</h2>}
        {/* Reference layout: title → short_description paragraph → summary bullets */}
        {showShortDesc && (
          <div className="foton-zigzag-desc">{renderRichDescription(featureDesc)}</div>
        )}
        {bullets.length > 0 && (
          <ul className="foton-zigzag-bullets">
            {bullets.map((bullet, bIdx) => (
              <li key={bIdx} className="foton-zigzag-bullet">
                <span className="foton-zigzag-bar" aria-hidden="true" />
                <div className="foton-zigzag-bullet-body">{renderContent(bullet)}</div>
              </li>
            ))}
          </ul>
        )}
      </div>
    );

    const mediaBlock = hasMainImage ? (
      <div className="foton-zigzag-media" data-aos="fade-up" data-aos-delay="120">
        <div
          className="foton-zigzag-card"
          role="button"
          tabIndex={0}
          onClick={() => setLightboxImg({ src: featureImg, caption: featureTitle })}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setLightboxImg({ src: featureImg, caption: featureTitle });
            }
          }}
          title={lang ? 'تكبير' : 'Enlarge'}
        >
          <img
            src={featureImg}
            alt={featureTitle || productName}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = productFallbackSrc;
            }}
          />
          {overlayCaption && (
            <span className="foton-zigzag-card-caption">{overlayCaption}</span>
          )}
        </div>
      </div>
    ) : null;

    return (
      <section
        key={key || item.id || sectionId || featureTitle}
        className={`foton-zigzag-row ${reverse ? 'is-reversed' : ''}`}
        id={sectionId || undefined}
      >
        <div className="foton-zigzag-inner">
          {reverse ? (
            <>
              {mediaBlock}
              {textBlock}
            </>
          ) : (
            <>
              {textBlock}
              {mediaBlock}
            </>
          )}
        </div>

        {galleryItems.length > 0 && (
          <div
            className={`foton-feature-gallery ${galleryItems.length > 3 ? 'is-carousel' : 'is-strip'}`}
            data-count={Math.min(galleryItems.length, 3)}
            data-aos="fade-up"
          >
            <div className="foton-feature-gallery-track">
              {galleryItems.map((gItem, gIdx) => {
                const src = getGalleryImageUrl(gItem.image);
                if (!src) return null;
                const caption = cleanText(
                  lang
                    ? (gItem.title_ar || gItem.title_en || '')
                    : (gItem.title_en || gItem.title_ar || '')
                );
                // Hide placeholder captions like "1", "2", "3"
                const showCaption = caption && !/^\d+$/.test(caption);
                return (
                  <button
                    type="button"
                    key={gItem.id || `fg-${gIdx}`}
                    className="foton-feature-gallery-card"
                    onClick={() => setLightboxImg({ src, caption: showCaption ? caption : featureTitle })}
                    title={showCaption ? caption : (lang ? 'تكبير' : 'Enlarge')}
                  >
                    <img
                      src={src}
                      alt={showCaption ? caption : `${featureTitle || productName} ${gIdx + 1}`}
                      loading="lazy"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = productFallbackSrc;
                      }}
                    />
                    {showCaption && (
                      <span className="foton-feature-gallery-caption">{caption}</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </section>
    );
  };

  /**
   * Render one CMS feature section in features[] / section_sort_order sequence.
   * Inside the section, rows follow item.sort_order (banner / 360 / zigzag interleaved).
   */
  const renderCmsFeatureSection = (feat, zigzagState) => {
    const slug = (feat?.slug || '').toLowerCase();
    if (!slug || slug === 'highlights') return null;

    if (slug === 'gallery') {
      if (!showProductGallery) return null;
      return (
        <section key="gallery" className="foton-product-gallery-section" id="gallery">
          <div className="foton-product-gallery-stage" data-aos="fade-up">
            <div className="foton-product-gallery-header">
              <span className="foton-product-gallery-tag">
                {lang ? 'معرض الصور' : 'GALLERY'}
              </span>
              <h2 className="foton-product-gallery-title">
                {lang ? `${productName} — المعرض` : `${productName} GALLERY`}
              </h2>
              <p className="foton-product-gallery-desc">
                {lang
                  ? 'استكشف مجموعة صور العرض لهذا الطراز.'
                  : 'Explore the showcase photos for this model.'}
              </p>
            </div>
            <div className="foton-product-gallery-mosaic">
              {galleryPhotos.map((photo, idx) => {
                const src = getGalleryImageUrl(photo.image);
                const caption = lang
                  ? (photo.title_ar || photo.title_en || `${productName} ${idx + 1}`)
                  : (photo.title_en || photo.title_ar || `${productName} ${idx + 1}`);
                return (
                  <button
                    type="button"
                    key={photo.id || idx}
                    className="foton-product-gallery-tile"
                    onClick={() => setLightboxImg({ src, caption, item: photo })}
                    title={lang ? 'انقر للتكبير' : 'Click to enlarge'}
                  >
                    <img
                      src={src}
                      alt={caption}
                      loading="lazy"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = productFallbackSrc;
                      }}
                    />
                  </button>
                );
              })}
            </div>
          </div>
        </section>
      );
    }

    // Highlights already rendered above as custom glance rows
    if (customHighlights && (slug === 'exterior' || slug === 'highlights')) {
      return null;
    }

    const rows = resolveFeatureContentRows(detailsData, feat);
    if (!rows.length && !isFeatureBannerEnabled(feat)) return null;

    let sectionIdAssigned = false;
    const takeSectionId = () => {
      if (sectionIdAssigned) return undefined;
      sectionIdAssigned = true;
      return slug;
    };

    // Master-level banner on features[] (rare) — only show if no row banner exists inside rows
    const hasRowBanner = rows.some((item) => isFeatureBannerEnabled(item) && !isDesign360Enabled(item));
    const masterBanner = !hasRowBanner ? resolveFeatureBannerData(feat, rows, lang) : null;

    return (
      <React.Fragment key={slug}>
        {masterBanner && renderFeatureBanner(masterBanner, takeSectionId())}
        {rows.map((item, idx) => {
          if (isDesign360Enabled(item)) {
            return renderDesign360Section(item, feat, takeSectionId());
          }

          if (isFeatureBannerEnabled(item)) {
            const banner = resolveFeatureBannerData(feat, [item], lang);
            return banner ? renderFeatureBanner(banner, takeSectionId()) : null;
          }

          // product_feature_gallery is rendered inside zigzag rows when present
          const reverse = zigzagState.count % 2 === 1;
          zigzagState.count += 1;
          return renderZigzagFeatureRow(item, feat, {
            sectionId: takeSectionId(),
            reverse,
            key: item.id || `${slug}-${idx}`,
          });
        })}
      </React.Fragment>
    );
  };

  const renderDesign360Section = (item, feat, sectionId) => {
    const frames = resolveDesign360Frames(item);
    if (!frames.length) return null;

    const title = lang
      ? (item.design_360_title_ar || item.feature_title_ar || item.feature_title_en || feat?.display_name_ar)
      : (item.design_360_title_en || item.feature_title_en || item.feature_title_ar || feat?.display_name_en);
    const watermark = cleanText(
      lang
        ? (item.design_360_watermark_ar || item.banner_overlay_text_ar || item.design_360_watermark_en || item.banner_overlay_text_en || '')
        : (item.design_360_watermark_en || item.banner_overlay_text_en || '')
    ) || cleanText((productName || '').split(/\s+/)[0] || productName || '');
    const specs = resolveDesign360Specs(item, lang, configs);
    const variants = resolveDesign360Variants(item);
    const frameKey = String(item.id || sectionId || '360');
    const frameIdx = Math.min(design360FrameIdx[frameKey] || 0, frames.length - 1);
    const currentFrame = frames[frameIdx] || frames[0];
    const canScrub = frames.length > 1;

    // Dial sits on the bottom arc of the ellipse (visual + scrub affordance)
    const dialT = canScrub ? frameIdx / Math.max(frames.length - 1, 1) : 0.5;
    const dialAngle = Math.PI * (0.15 + dialT * 0.7); // bottom-ish arc
    const dialX = 50 + Math.cos(dialAngle) * 42;
    const dialY = 58 + Math.sin(dialAngle) * 18;

    const setFrame = (next) => {
      const max = frames.length - 1;
      const clamped = ((next % frames.length) + frames.length) % frames.length;
      setDesign360FrameIdx((prev) => ({ ...prev, [frameKey]: Math.min(clamped, max) }));
    };

    const onPointerDown = (clientX) => {
      if (!canScrub) return;
      design360DragRef.current = {
        active: true,
        key: frameKey,
        startX: clientX,
        startIdx: frameIdx,
      };
    };

    const onPointerMove = (clientX) => {
      const drag = design360DragRef.current;
      if (!drag.active || drag.key !== frameKey || !canScrub) return;
      const delta = clientX - drag.startX;
      const step = Math.round(delta / 28);
      setFrame(drag.startIdx + step);
    };

    const onPointerUp = () => {
      design360DragRef.current = { active: false, key: null, startX: 0, startIdx: 0 };
    };

    const thumbs = variants.length > 0
      ? variants
      : [{ id: 'current', src: currentFrame.src, label: title }];

    return (
      <section
        key={`360-${frameKey}`}
        className="foton-design360-section"
        id={sectionId || undefined}
      >
        <div className="foton-design360-container" data-aos="fade-up">
          <h2 className="foton-design360-title">{title || 'PRODUCT 360'}</h2>

          <div
            className={`foton-design360-stage ${canScrub ? 'is-interactive' : ''}`}
            onMouseDown={(e) => onPointerDown(e.clientX)}
            onMouseMove={(e) => onPointerMove(e.clientX)}
            onMouseUp={onPointerUp}
            onMouseLeave={onPointerUp}
            onTouchStart={(e) => onPointerDown(e.touches[0]?.clientX || 0)}
            onTouchMove={(e) => onPointerMove(e.touches[0]?.clientX || 0)}
            onTouchEnd={onPointerUp}
            role={canScrub ? 'slider' : undefined}
            aria-valuemin={canScrub ? 0 : undefined}
            aria-valuemax={canScrub ? frames.length - 1 : undefined}
            aria-valuenow={canScrub ? frameIdx : undefined}
            aria-label={lang ? 'عرض 360 درجة' : '360 degree view'}
            tabIndex={canScrub ? 0 : undefined}
            onKeyDown={(e) => {
              if (!canScrub) return;
              if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
                e.preventDefault();
                setFrame(frameIdx + (e.key === 'ArrowRight' ? 1 : -1));
              }
            }}
          >
            {watermark && (
              <div className="foton-design360-watermark" aria-hidden="true">{watermark}</div>
            )}

            <img
              src={currentFrame.src}
              alt={title || productName}
              className="foton-design360-image"
              draggable={false}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = productFallbackSrc;
              }}
            />

            <div className="foton-design360-orbit" aria-hidden="true">
              <svg className="foton-design360-orbit-svg" viewBox="0 0 100 100" preserveAspectRatio="none">
                <ellipse cx="50" cy="58" rx="42" ry="18" />
              </svg>
              <span
                className="foton-design360-dial"
                style={{ left: `${dialX}%`, top: `${dialY}%` }}
              />
            </div>
          </div>

          <div className="foton-design360-footer">
            <div className="foton-design360-specs">
              {specs.map((spec, idx) => (
                <div key={`${spec.label}-${idx}`} className="foton-design360-spec">
                  <div className="foton-design360-spec-label">{spec.label}</div>
                  <div className="foton-design360-spec-value">{spec.value}</div>
                </div>
              ))}
            </div>

            <div className="foton-design360-thumbs">
              {thumbs.map((thumb) => (
                <button
                  key={thumb.id}
                  type="button"
                  className="foton-design360-thumb"
                  onClick={() => setLightboxImg({ src: thumb.src, caption: thumb.label || title })}
                  title={thumb.label || (lang ? 'تكبير' : 'Enlarge')}
                >
                  <img src={thumb.src} alt={thumb.label || ''} draggable={false} />
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  };

  // Unified glance gallery for first highlight row: feature vehicle_image + product galleries
  const glanceSlides = (() => {
    const slides = [];
    if (customHighlights && primaryHighlight?.vehicle_image) {
      slides.push({
        id: `highlight-vehicle-${primaryHighlight.id || 0}`,
        src: getFeatureImageUrl(primaryHighlight),
        title_en: primaryHighlight.feature_title_en,
        title_ar: primaryHighlight.feature_title_ar,
      });
    }
    productGalleries.forEach((g) => {
      if (!g?.image) return;
      slides.push({
        id: g.id,
        src: getGalleryImageUrl(g.image),
        title_en: g.title_en,
        title_ar: g.title_ar,
      });
    });
    if (slides.length === 0 && detailsData.title.product_image) {
      slides.push({
        id: 'product-image',
        src: getProductImageUrl(detailsData.title.product_image),
        title_en: productName,
        title_ar: productName,
      });
    }
    return slides;
  })();

  const currentGalleryItem = glanceSlides[selectedGalleryIdx] || glanceSlides[0];

  const splitHeroTitleLines = (text) => {
    const upper = text.toUpperCase();
    if (upper.includes('SMALL BUSINESS') && upper.includes('BIG DREAM')) {
      return (
        <>
          <span className="hero-title-line">SMALL BUSINESS,</span>
          <span className="hero-title-line">BIG DREAM</span>
        </>
      );
    }
    if (upper.includes('EMPOWERING YOUR DELIVERY') && upper.includes('LOGISTICS BUSINESS')) {
      return (
        <>
          <span className="hero-title-line">EMPOWERING YOUR DELIVERY</span>
          <span className="hero-title-line">LOGISTICS BUSINESS.</span>
        </>
      );
    }
    if (upper.includes('THE BEST CHOICE FOR URBAN LOGISTICS') && upper.includes('TRANSPORTATION')) {
      return (
        <>
          <span className="hero-title-line">THE BEST CHOICE FOR URBAN LOGISTICS</span>
          <span className="hero-title-line">AND TRANSPORTATION.</span>
        </>
      );
    }
    // Split on comma / period when CMS sends a multi-part slogan
    const parts = text.split(/(?<=[,.])\s+/).map((p) => p.trim()).filter(Boolean);
    if (parts.length > 1) {
      return parts.map((line, idx) => (
        <span className="hero-title-line" key={idx}>{line}</span>
      ));
    }
    return text;
  };

  const renderBannerTitle = () => {
    // Prefer CMS product_slider_title (e.g. "SMALL BUSINESS, BIG DREAM") over short_description
    const text = productSliderTitle || productShortDesc || productName;
    if (!text) return productName;
    return splitHeroTitleLines(text);
  };

  return (
    <div className={`foton-vehicle-page ${customHighlights ? 'foton-aumark-page' : ''} ${lang ? 'foton-rtl' : ''}`}>
      {/* ====================================================================
          1. Hero Banner (.banner) - 100% Dynamic API Data matching Reference Design
          ==================================================================== */}
      <section className="foton-hero-banner">
        <img
          src={heroImage}
          alt={productName}
          className="hero-bg-img"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = productFallbackSrc;
          }}
        />
        <div className="hero-gradient-overlay" />

        <div className="foton-hero-content" data-aos="fade-up">
          <h1 className="foton-hero-title">
            {renderBannerTitle()}
          </h1>
        </div>
      </section>

      {/* ====================================================================
          2. Sticky Subnavigation Bar (.twoMenu) - Dynamic Tabs
          ==================================================================== */}
      <nav className={`foton-sticky-subnav ${isSubnavStuck ? 'is-stuck' : ''}`}>
        <div className="foton-subnav-inner">
          <ul className="foton-subnav-tabs">
            {navTabs.map((tab) => (
              <li
                key={tab.slug}
                className={`foton-subnav-tab ${activeTab === tab.slug ? 'active' : ''}`}
                onClick={(e) => handleTabClick(e, tab.slug)}
              >
                {lang ? tab.titleAr : tab.titleEn}
              </li>
            ))}
          </ul>

          <Link to="/request-a-quote" className="foton-subnav-cta">
            {lang ? 'طلب عرض سعر' : 'REQUEST A QUOTE'}
          </Link>
        </div>
      </nav>

      {/* ====================================================================
          3. Breadcrumbs (.history) - Dynamic Product Name
          ==================================================================== */}
      <div className="foton-history-bar">
        <Link to="/">
          <FaHome style={{ marginRight: '4px', verticalAlign: '-1px' }} />
          {lang ? 'الرئيسية' : 'HOME'}
        </Link>
        <span className="crumb-sep">
          <FaChevronRight style={{ transform: lang ? 'rotate(180deg)' : 'none' }} />
        </span>
        <Link to={detailsData.title.menus_slug ? `/vehicles/${detailsData.title.menus_slug}` : '/vehicles'}>
          {lang ? 'المنتجات' : 'PRODUCTS'}
        </Link>
        <span className="crumb-sep">
          <FaChevronRight style={{ transform: lang ? 'rotate(180deg)' : 'none' }} />
        </span>
        <span className="crumb-current">{productName}</span>
      </div>

      {/* ====================================================================
          4. Highlights / Glance Section — render EVERY Highlights object
          Design: title + | bullets (left) / vehicle image (right), alternating rows
          ==================================================================== */}
      {customHighlights ? (
        <>
        {renderFeatureBanner(highlightsBanner, highlightsTabSlug)}
        {highlightItems.map((item, idx) => {
          const featureTitle = lang
            ? (item.feature_title_ar || item.feature_title_en)
            : (item.feature_title_en || item.feature_title_ar);
          const featureDesc = lang
            ? (item.short_description_ar || item.short_description_en)
            : (item.short_description_en || item.short_description_ar);
          const summaries = getFeatureSummaries(item, lang);
          const featureImg = item.vehicle_image && !isStaticSeededImage(item.vehicle_image)
            ? getFeatureImageUrl(item)
            : null;
          const isFirst = idx === 0;
          const isReversed = idx % 2 === 1;
          // Only show gallery thumbnails if this specific feature actually has its own product_feature_gallery
          const itemFeatureGallery = Array.isArray(item.product_feature_gallery)
            ? item.product_feature_gallery.filter((g) => g && g.image && !isStaticSeededImage(g.image))
            : [];
          const hasItemGallery = itemFeatureGallery.length > 0;
          const mainSrc = featureImg;
          const mainCaption = featureTitle;
          const sectionId = !highlightsBanner && isFirst ? highlightsTabSlug : undefined;

          return (
            <section
              key={item.id || `highlight-${idx}`}
              className={`foton-glance-section ${isReversed ? 'foton-highlight-row-alt' : ''}`}
              id={sectionId}
            >
              <div className={`foton-glance-container ${isReversed ? 'foton-highlight-reverse' : ''}`}>
          <div className="foton-glance-text" data-aos="fade-up">
                  {isFirst && !highlightsBanner && (
            <span className="foton-glance-badge">
                      {item.tab_title_en || (lang ? 'نظرة سريعة' : 'OVERVIEW')}
            </span>
                  )}
                  <h2 className="foton-glance-heading">{featureTitle}</h2>

                  {hasMeaningfulText(featureDesc) && (
                    <div className="foton-highlight-short-desc">
                      {renderRichDescription(featureDesc)}
                    </div>
                  )}

                  {summaries.length > 0 && (
                    <div className="foton-glance-bullets">
                      {summaries.map((summary, sIdx) => (
                        <div key={sIdx} className="foton-glance-bullet">
                      <span className="bullet-bar">|</span>
                          <div>{renderContent(summary)}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="foton-glance-gallery" data-aos="fade-up" data-aos-delay="150">
                  {mainSrc && (
                    <div
                      className="foton-glance-main-card"
                      onClick={() => setLightboxImg({ src: mainSrc, caption: mainCaption, item })}
                      style={{ cursor: 'pointer' }}
                      title={lang ? 'انقر للتكبير' : 'Click to enlarge'}
                    >
                      <img
                        src={mainSrc}
                        alt={featureTitle || productName}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = productFallbackSrc;
                        }}
                      />
                    </div>
                  )}

                  {hasItemGallery && itemFeatureGallery.length > 1 && (
                    <div className="foton-glance-thumbs">
                      {itemFeatureGallery.map((slide, sIdx) => {
                        const thumbSrc = getGalleryImageUrl(slide.image);
                        return (
                          <div
                            key={slide.id || sIdx}
                            className="foton-glance-thumb"
                            onClick={() =>
                              setLightboxImg({
                                src: thumbSrc,
                                caption: lang ? slide.title_ar : slide.title_en,
                                item: slide
                              })
                            }
                          >
                            <img
                              src={thumbSrc}
                              alt={`Gallery thumbnail ${sIdx + 1}`}
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = productFallbackSrc;
                              }}
                            />
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </section>
          );
        })}
        </>
      ) : showGlanceOverview ? (
        <section className="foton-glance-section" id={highlightsTabSlug}>
          <div className="foton-glance-container">
            <div className="foton-glance-text" data-aos="fade-up">
              <span className="foton-glance-badge">
                {lang ? 'نظرة سريعة' : 'OVERVIEW'}
              </span>
              <h2 className="foton-glance-heading">
                {`${productName} ${lang ? 'في لمحة' : 'AT A GLANCE'}`}
              </h2>

              <div className="foton-glance-bullets">
                  {productShortDesc && (
                    <div className="foton-glance-bullet">
                      <span className="bullet-bar">|</span>
                    <div>{renderRichDescription(productShortDesc)}</div>
                    </div>
                  )}

                  {configs.slice(0, 4).map((cfg, idx) => {
                    const label = lang
                      ? (cfg.label_name_ar || cfg.label_name_en)
                      : (cfg.label_name_en || cfg.label_name_ar);
                    const val = lang
                      ? (cfg.value_ar || cfg.value_en)
                      : (cfg.value_en || cfg.value_ar);

                    return (
                      <div key={cfg.id || idx} className="foton-glance-bullet">
                        <span className="bullet-bar">|</span>
                        <div>
                          <strong>{label}:</strong> {val}
                        </div>
                      </div>
                    );
                  })}

                {(() => {
                  const glanceHint = sortFeatureContentRows(detailsData.Exterior || [])[0];
                  if (!glanceHint || !hasMeaningfulText(glanceHint.short_description_en)) return null;
                  return (
                    <div className="foton-glance-bullet">
                      <span className="bullet-bar">|</span>
                      <div>
                        {cleanText(
                          lang
                            ? (glanceHint.short_description_ar || glanceHint.short_description_en)
                            : glanceHint.short_description_en
                        )}
                      </div>
                    </div>
                  );
                })()}
              </div>
          </div>

          <div className="foton-glance-gallery" data-aos="fade-up" data-aos-delay="150">
              {currentGalleryItem?.src && (
            <div
              className="foton-glance-main-card"
              onClick={() => {
                    if (currentGalleryItem?.src) {
                  setLightboxImg({
                        src: currentGalleryItem.src,
                    caption: lang ? currentGalleryItem?.title_ar : currentGalleryItem?.title_en,
                  });
                }
              }}
              style={{ cursor: 'pointer' }}
              title={lang ? 'انقر للتكبير' : 'Click to enlarge'}
            >
              <img
                    src={currentGalleryItem.src}
                alt={productName}
                onError={(e) => {
                  e.target.onerror = null;
                      e.target.src = productFallbackSrc;
                }}
              />
            </div>
              )}

              {glanceSlides.length > 1 && (
              <div className="foton-glance-thumbs">
                  {glanceSlides.map((slide, idx) => (
                  <div
                    key={slide.id || idx}
                    className={`foton-glance-thumb ${idx === selectedGalleryIdx ? 'active' : ''}`}
                    onClick={() => setSelectedGalleryIdx(idx)}
                  >
                    <img
                        src={slide.src}
                      alt={`Gallery thumbnail ${idx + 1}`}
                      onError={(e) => {
                        e.target.onerror = null;
                          e.target.src = productFallbackSrc;
                      }}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
      ) : null}

      {/* ====================================================================
          CMS FEATURE SECTIONS — loop data.features[] (section_sort_order)
          Inside each tab: rows by item.sort_order (banner / 360 / zigzag)
          ==================================================================== */}
      {(() => {
        const zigzagState = { count: 0 };
        const rendered = orderedMasterFeatures.map((feat) =>
          renderCmsFeatureSection(feat, zigzagState)
        );
        // Gallery fallback when enabled but not listed in features[]
        const galleryInFeatures = orderedMasterFeatures.some(
          (f) => (f.slug || '').toLowerCase() === 'gallery'
        );
        if (showProductGallery && !galleryInFeatures) {
          rendered.push(renderCmsFeatureSection({ slug: 'gallery', display_name_en: 'Gallery' }, zigzagState));
        }
        return rendered;
      })()}

      {/* ====================================================================
          12. SPECIFICATIONS — only when product_configurations is not empty
              Titles from product_configuraiton_title (per product CMS)
          ==================================================================== */}
      <section className={`foton-specs-section ${configs.length === 0 ? 'foton-specs-actions-only' : ''}`} id="specifications">
        <div className="foton-specs-container">
          {configs.length > 0 && (() => {
            const specsMainTitle = lang
              ? (configTitleMeta?.category_title_ar || configTitleMeta?.category_title_en || 'مواصفات وتجهيزات الشاحنة')
              : (configTitleMeta?.category_title_en || configTitleMeta?.category_title_ar || 'SPECIFICATIONS & CONFIGURATIONS');
            const specsHighlightRaw = lang
              ? (configTitleMeta?.highlight_ar || configTitleMeta?.highlight_en)
              : (configTitleMeta?.highlight_en || configTitleMeta?.highlight_ar);
            const specsDescRaw = lang
              ? (configTitleMeta?.description_ar || configTitleMeta?.description_en)
              : (configTitleMeta?.description_en || configTitleMeta?.description_ar);
            const specsDesc =
              (hasMeaningfulText(specsHighlightRaw) && cleanText(specsHighlightRaw)) ||
              (hasMeaningfulText(specsDescRaw) && cleanText(specsDescRaw)) ||
              (lang
                ? `كافة المواصفات والتجهيزات الفنية لـ ${productName}`
                : `Comprehensive technical specifications for ${productName}`);

            return (
            <>
              <div className="foton-specs-header" data-aos="fade-up">
                <span className="foton-specs-tag">
                  {lang ? 'المواصفات الفنية' : 'TECHNICAL SPECIFICATIONS'}
                </span>
                  <h2 className="foton-specs-title">{specsMainTitle}</h2>
                  {specsDesc && (
                    <p className="foton-specs-desc">{specsDesc}</p>
                  )}
              </div>

              <div className="foton-specs-grid" data-aos="fade-up">
                {configs.map((cfg, idx) => {
                  const label = lang
                    ? (cfg.label_name_ar || cfg.label_name_en)
                    : (cfg.label_name_en || cfg.label_name_ar);
                  const val = lang
                    ? (cfg.value_ar || cfg.value_en)
                    : (cfg.value_en || cfg.value_ar);

                    if (!label && !val) return null;

                  return (
                    <div key={cfg.id || idx} className="foton-spec-card">
                        {label && <span className="foton-spec-label">{label}</span>}
                        {val && <span className="foton-spec-value">{val}</span>}
                    </div>
                  );
                })}
              </div>
            </>
            );
          })()}

          {/* Dynamic CTAs Action Bar from api/getFormMenus */}
          <div className="foton-specs-actions" data-aos="fade-up">
            {(formMenus || []).map((item, idx) => {
              const title = lang
                ? (item.menu_name_ar || item.menu_name_en)
                : (item.menu_name_en || item.menu_name_ar);
              const link = resolveFormLink(item);
              const btnClass = idx === 0 ? 'foton-btn-primary' : 'foton-btn-secondary';
              const icon = getDetailBtnIcon(item);

              return (
                <Link key={item.id || idx} to={link} className={btnClass}>
                  {icon}
                  <span>{(title || '').toUpperCase()}</span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* ====================================================================
          11. Lightbox Image Modal (.zhezhao)
          ==================================================================== */}
      {lightboxImg && (
        <div
          className="foton-lightbox-backdrop"
          onClick={() => setLightboxImg(null)}
        >
          <div
            className="foton-lightbox-content"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="foton-lightbox-close"
              onClick={() => setLightboxImg(null)}
              aria-label="Close"
            >
              <FaTimes />
            </button>
            <div className="foton-lightbox-img-wrap" style={{ position: 'relative' }}>
              <img src={lightboxImg.src} alt="Enlarged view" />
              {lightboxImg.item ? (
                <BannerCaptionOverlay
                  item={lightboxImg.item}
                  isRtl={Boolean(lang)}
                />
              ) : null}
            </div>
            {lightboxImg.caption && !lightboxImg.item && (
              <div
                className="foton-lightbox-caption"
                dangerouslySetInnerHTML={{ __html: lightboxImg.caption }}
              />
            )}
          </div>
        </div>
      )}

      {/* Global Footer */}
      <Footer />
    </div>
  );
}

export default Inside;
